import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";

// registry TrainingGroup ↔ training-infra Group 매핑 엔티티. github_team_names 는 멤버
// Role.team_name 과 매칭되는 GitHub 팀명 목록이다. 팀명은 별도 sub-resource 로 추가/제거한다.
// 응답에는 nested 로 포함된다.
export type TrainingGroup = {
  uuid: string;
  name: string;
  description: string | null;
  training_group_id: string | null;
  github_team_names: readonly string[];
  created_at: string;
  modified_at: string;
};

// Group↔training-infra Group 매핑 드롭다운 후보(training `GET /groups` 라이브 프록시). id 를
// 매핑값으로 저장한다. `/admin/training-groups/candidates` 가 이 값을 반환한다.
export type TrainingGroupCandidate = {
  id: string;
  name: string;
};

export type TrainingGroupCreateReq = {
  name: string;
  description?: string | null;
  training_group_id?: string | null;
};

export type TrainingGroupUpdateReq = {
  name?: string;
  description?: string | null;
  training_group_id?: string | null;
};

export async function listTrainingGroups(): Promise<TrainingGroup[]> {
  const response = await apiClient.get<TrainingGroup[]>(apiRoutes.admin.trainingGroups);
  return response.data;
}

export async function createTrainingGroup(payload: TrainingGroupCreateReq): Promise<TrainingGroup> {
  const response = await apiClient.post<TrainingGroup>(apiRoutes.admin.trainingGroups, payload);
  return response.data;
}

export async function updateTrainingGroup(
  groupUuid: string,
  payload: TrainingGroupUpdateReq,
): Promise<TrainingGroup> {
  const response = await apiClient.patch<TrainingGroup>(
    apiRoutes.admin.trainingGroupDetail(groupUuid),
    payload,
  );
  return response.data;
}

export async function deleteTrainingGroup(groupUuid: string): Promise<void> {
  await apiClient.delete(apiRoutes.admin.trainingGroupDetail(groupUuid));
}

// GitHub 팀 매핑을 추가하고 갱신된 Group 을 반환한다. 이름이 전역으로 중복되면
// 409(isDuplicateGithubTeamError)다.
export async function addTrainingGroupGithubTeam(
  groupUuid: string,
  githubTeamName: string,
): Promise<TrainingGroup> {
  const response = await apiClient.post<TrainingGroup>(
    apiRoutes.admin.trainingGroupGithubTeams(groupUuid),
    { github_team_name: githubTeamName },
  );
  return response.data;
}

export async function removeTrainingGroupGithubTeam(
  groupUuid: string,
  githubTeamName: string,
): Promise<void> {
  await apiClient.delete(apiRoutes.admin.trainingGroupGithubTeamDetail(groupUuid, githubTeamName));
}

// 유저 → TrainingGroup 명시 매핑(a축) 한 건. 가입 전에도 매핑할 수 있다.
// 가입 후 서버가 member_uuid 를 연결한다.
export type GroupMemberView = {
  github_user_id: number;
  github_login: string;
  registered: boolean;
  member_uuid: string | null;
};

// 유저(github_user_id)를 TrainingGroup 에 명시 매핑한다. 중복은 409(isDuplicateGroupMemberError)다.
// 매핑 현황은 org 디렉터리 응답에 포함된 registryGroups 로 표시한다. 별도 목록은 조회하지 않는다.
export async function addTrainingGroupMember(
  groupUuid: string,
  payload: { github_user_id: number; github_login: string },
): Promise<GroupMemberView> {
  const response = await apiClient.post<GroupMemberView>(
    apiRoutes.admin.trainingGroupMembers(groupUuid),
    payload,
  );
  return response.data;
}

export async function removeTrainingGroupMember(
  groupUuid: string,
  githubUserId: number,
): Promise<void> {
  await apiClient.delete(apiRoutes.admin.trainingGroupMemberDetail(groupUuid, githubUserId));
}

// Group↔training-infra Group 매핑 드롭다운 후보. training 라이브 프록시라 실패하면 upstream.*
// 오류가 올 수 있다.
export async function listTrainingGroupCandidates(): Promise<TrainingGroupCandidate[]> {
  const response = await apiClient.get<TrainingGroupCandidate[]>(
    apiRoutes.admin.trainingGroupCandidates,
  );
  return response.data;
}

// Group 의 두 UNIQUE 축 중복(409 training_group.duplicate). create 와 update 가 함께 쓴다.
// 축은 `name`(사람이 읽는 팀명)과 `training_group_id`(한 training Group 당 그룹 하나)다.
// 호출부는 자기가 보낸 필드에 따라 문구를 고른다.
export function isDuplicateTrainingGroupError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_group.duplicate";
}

// GitHub 팀명 전역 중복(409 training_group.github_team_duplicate).
export function isDuplicateGithubTeamError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_group.github_team_duplicate";
}

// 유저↔group 명시 매핑 중복(409 training_group_member.duplicate).
export function isDuplicateGroupMemberError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training_group_member.duplicate";
}
